/**
 * Suma de verificacion de los respaldos (Req. 3.2).
 *
 * Permite detectar un archivo corrupto, truncado o editado a mano ANTES de
 * reemplazar los datos del dispositivo.
 *
 * Usa SHA-256 de `crypto.subtle`, disponible de forma nativa en el navegador
 * y en Node, sin agregar dependencias.
 */

/**
 * Serializa de forma DETERMINISTA: las claves de cada objeto se ordenan
 * alfabeticamente.
 *
 * Sin este orden, dos serializaciones del mismo contenido podrian producir
 * cadenas distintas y, por lo tanto, sumas distintas, lo que haria que un
 * respaldo valido pareciera corrupto.
 */
export function serializarDeterminista(valor: unknown): string {
  if (valor === null || typeof valor !== 'object') {
    return JSON.stringify(valor) ?? 'null';
  }

  if (Array.isArray(valor)) {
    return `[${valor.map(serializarDeterminista).join(',')}]`;
  }

  const registro = valor as Record<string, unknown>;
  const partes = Object.keys(registro)
    .sort()
    .filter((clave) => registro[clave] !== undefined)
    .map((clave) => `${JSON.stringify(clave)}:${serializarDeterminista(registro[clave])}`);

  return `{${partes.join(',')}}`;
}

function aHexadecimal(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Calcula la suma de verificacion. Devuelve `"sha256:<hexadecimal>"`. */
export async function calcularChecksum(valor: unknown): Promise<string> {
  const texto = serializarDeterminista(valor);
  const bytes = new TextEncoder().encode(texto);
  const digestion = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return `sha256:${aHexadecimal(digestion)}`;
}

/** Compara en tiempo constante para no filtrar informacion por el tiempo. */
export function checksumCoincide(esperado: string, calculado: string): boolean {
  if (esperado.length !== calculado.length) return false;
  let diferencia = 0;
  for (let i = 0; i < esperado.length; i++) {
    diferencia |= esperado.charCodeAt(i) ^ calculado.charCodeAt(i);
  }
  return diferencia === 0;
}
